import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { createPortal } from "react-dom"
import {
  Gauge,
  Package,
  CreditCard,
  FileText,
  GearSix,
  CaretDown,
  User,
  TrafficCone,
  CaretRight,
  CaretLeft,
  ChartBar,
  CurrencyCircleDollar,
  Ticket,
  Graph,
  UsersThree,
  ShieldCheck,
  ClockCounterClockwise,
} from "phosphor-react"
import { AnimatePresence, motion } from "framer-motion"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import { useCurrentUser } from "../../utils/context/UserProvider"
import leadsService from "../../services/leadsService"
import logo from "../../assets/images/logo.png"
import logosmall from "../../assets/images/logosmall.png"

const cx = (...a) => a.filter(Boolean).join(" ")
const SIDEBAR_COLLAPSED_PX = 88 // ~ 5.5rem

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { checkAccess } = useAccessChecker()
  const { currentUser } = useCurrentUser()
  const location = useLocation()

  const [expandedItems, setExpandedItems] = useState({})

  // ✅ Leads unread notification (loading only first time)
  const [leadsUnreadCount, setLeadsUnreadCount] = useState(0)
  const [leadsUnreadLoading, setLeadsUnreadLoading] = useState(true)
  const leadsReadyRef = useRef(false)
  const reqIdRef = useRef(0)

  // ✅ Collapsed flyout (portal so it never gets clipped)
  const [flyout, setFlyout] = useState(null) // { title, items, rect }
  const flyoutCloseT = useRef(null)

  // ✅ Collapsed tooltip (portal + framer motion)
  const [tip, setTip] = useState(null) // { text, top, left }
  const tipAnchorRef = useRef(null)
  const tipCloseT = useRef(null)

  const isPathActive = useCallback(
    (path) => location.pathname === path || location.pathname.startsWith(path + "/"),
    [location.pathname],
  )

  const menuItems = useMemo(() => {
    const settingsSub = [
      {
        path: "/settings/account",
        name: "Account Settings",
        icon: <User size={16} />,
        access: checkAccess("settings", "read"),
      },
      {
        path: "/settings/users",
        name: "User Management",
        icon: <UsersThree size={16} />,
        access: checkAccess("users", "read"),
      },
      {
        path: "/settings/providers",
        name: "Providers",
        icon: <Package size={16} />,
        access: checkAccess("providers", "read"),
      },
      {
        path: "/settings/roles",
        name: "Role Management",
        icon: <ShieldCheck size={16} />,
        access: checkAccess("roles", "read"),
      },
      {
        path: "/settings/payments-info",
        name: "Payments Info",
        icon: <CreditCard size={16} />,
        access: currentUser?.role?.name === "SuperAdmin" && currentUser?.isOwner,
      },
      {
        path: "/settings/audit-log",
        name: "Audit Log",
        icon: <ClockCounterClockwise size={16} />,
        access: currentUser?.role?.name === "SuperAdmin",
      },
    ].filter((x) => x.access)

    return [
      { path: "/", name: "Dashboard", icon: <Gauge size={20} />, access: checkAccess("dashboard", "read") },
      { path: "/clients", name: "Clients", icon: <User size={20} />, access: checkAccess("clients", "read") },
      { path: "/financial-stats", name: "Statistics", icon: <Graph size={20} />, access: checkAccess("statistics", "read") },
      { path: "/ad-accounts", name: "Accounts List", icon: <Package size={20} />, access: checkAccess("adAccounts", "read") },
      { path: "/credit-accounts", name: "Balance Tracking", icon: <CreditCard size={20} />, access: checkAccess("creditsAccounts", "read") },
      { path: "/credit-accounts-history", name: "Balance Records", icon: <FileText size={20} />, access: checkAccess("creditsAccountsHistory", "read") },
      { path: "/top-ups", name: "Financial Report", icon: <ChartBar size={20} />, access: checkAccess("topups", "read") },
      { path: "/expenses", name: "Assets", icon: <CurrencyCircleDollar size={20} />, access: checkAccess("expenses", "read") },
      { path: "/tickets", name: "Tickets", icon: <Ticket size={20} />, access: checkAccess("tickets", "read") },
      {
        path: "/leads",
        name: "Leads",
        icon: <TrafficCone size={20} />,
        access: checkAccess("Leads", "read"),
        badge: leadsUnreadCount,
      },
      {
        path: "/settings",
        name: "Settings",
        icon: <GearSix size={20} />,
        access: true,
        subItems: settingsSub,
      },
    ]
  }, [checkAccess, currentUser?.role?.name, currentUser?.isOwner, leadsUnreadCount])

  // ✅ Expand settings if user is inside settings
  useEffect(() => {
    if (collapsed) return
    if (location.pathname.startsWith("/settings")) {
      setExpandedItems((p) => (p["/settings"] ? p : { ...p, "/settings": true }))
    }
  }, [collapsed, location.pathname])

  const toggleExpand = (path, e) => {
    e.preventDefault()
    e.stopPropagation()
    setCollapsed(false)
    setExpandedItems((prev) => ({ ...prev, [path]: !prev[path] }))
  }

  // ✅ Unread count fetch (only show loading on first load)
  const fetchLeadsUnread = useCallback(
    async ({ initial = false } = {}) => {
      const hasAccess = checkAccess("Leads", "read")
      if (!hasAccess) {
        setLeadsUnreadCount(0)
        setLeadsUnreadLoading(false)
        leadsReadyRef.current = true
        return
      }

      const reqId = ++reqIdRef.current
      if (initial && !leadsReadyRef.current) setLeadsUnreadLoading(true)

      try {
        const res = await leadsService.unreadCount()
        if (reqId !== reqIdRef.current) return
        const count = res?.data?.data?.count ?? res?.data?.count ?? 0
        setLeadsUnreadCount(Number(count) || 0)
      } catch {
        // silent
      } finally {
        if (initial && !leadsReadyRef.current) setLeadsUnreadLoading(false)
        leadsReadyRef.current = true
      }
    },
    [checkAccess],
  )

  useEffect(() => {
    fetchLeadsUnread({ initial: true })

    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchLeadsUnread({ initial: false })
    }, 30_000)

    const onFocus = () => fetchLeadsUnread({ initial: false })
    const onVis = () => document.visibilityState === "visible" && fetchLeadsUnread({ initial: false })

    window.addEventListener("focus", onFocus)
    document.addEventListener("visibilitychange", onVis)

    return () => {
      clearInterval(id)
      window.removeEventListener("focus", onFocus)
      document.removeEventListener("visibilitychange", onVis)
    }
  }, [fetchLeadsUnread])

  const LeadsBadge = ({ count }) => {
    if (!leadsReadyRef.current && leadsUnreadLoading) {
      return (
        <span className="ml-auto inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] bg-yellow-500 text-black">
          <span className="h-2.5 w-8 rounded bg-black/20 animate-pulse" />
        </span>
      )
    }
    if (!count || count <= 0) return null
    return (
      <span className="ml-auto inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] bg-yellow-500 text-black font-[500]">
        {count > 99 ? "99+" : count}
      </span>
    )
  }

  // ✅ Flyout handlers
  const openFlyout = (item, el) => {
    if (!collapsed) return
    if (!item?.subItems?.length) return
    if (flyoutCloseT.current) clearTimeout(flyoutCloseT.current)
    setFlyout({ title: item.name, items: item.subItems, rect: el.getBoundingClientRect() })
  }

  const scheduleCloseFlyout = () => {
    if (flyoutCloseT.current) clearTimeout(flyoutCloseT.current)
    flyoutCloseT.current = setTimeout(() => setFlyout(null), 120)
  }

  useEffect(() => () => flyoutCloseT.current && clearTimeout(flyoutCloseT.current), [])

  // ✅ Tooltip handlers (works reliably with portal)
  const computeTip = useCallback(() => {
    const el = tipAnchorRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    return {
      top: r.top + r.height / 2,
      left: r.right + 14,
    }
  }, [])

  const openTip = useCallback(
    (text, el) => {
      if (!collapsed) return
      if (!text || !el) return
      if(text === 'Settings') return
      if (tipCloseT.current) clearTimeout(tipCloseT.current)
      tipAnchorRef.current = el
      const pos = computeTip()
      setTip(pos ? { text, ...pos } : { text, top: 80, left: SIDEBAR_COLLAPSED_PX + 14 })
    },
    [collapsed, computeTip],
  )

  const closeTip = useCallback(() => {
    if (tipCloseT.current) clearTimeout(tipCloseT.current)
    tipCloseT.current = setTimeout(() => setTip(null), 80)
  }, [])

  useEffect(() => {
    if (!collapsed) setTip(null)
  }, [collapsed])

  useEffect(() => {
    if (!tip) return
    const update = () => {
      const pos = computeTip()
      if (!pos) return
      setTip((p) => (p ? { ...p, ...pos } : p))
    }

    window.addEventListener("scroll", update, true) // capture (works with nested scroll areas)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [tip, computeTip])

  useEffect(() => () => tipCloseT.current && clearTimeout(tipCloseT.current), [])

  return (
    <div
      className={cx(
        "h-screen bg-neutral-950 text-start starry-background border-r border-neutral-150 dark:border-neutral-900 transition-all duration-300 fixed left-0 top-0 z-30 overflow-x-hidden",
        collapsed ? "w-0 lg:w-[5.5rem]" : "w-60",
      )}
    >
      {/* Header */}
      <div
        className={cx(
          "flex items-center h-16 border-b border-neutral-900 py-4 w-full",
          collapsed ? "px-2 justify-center" : "px-7 justify-between",
        )}
      >
        {!collapsed ? (
          <Link to="/" className="flex items-center cursor-pointer w-full  justify-center gap-2">
            <img src={logo || "/placeholder.svg"} className="h-5 mt-1" alt="logo" />
          </Link>
        ) : (
          <Link to="/" className="flex cursor-pointer items-center">
            <img src={logosmall || "/placeholder.svg"} className="h-12 w-12" alt="logo" />
          </Link>
        )}

        <motion.button
          onClick={() => setCollapsed(!collapsed)}
          className="lg:hidden rounded-full text-neutral-500 transition-all duration-200"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          whileTap={{ scale: 0.98 }}
        >
          {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
        </motion.button>
      </div>

      {/* Nav */}
      <div
        className={cx(
          "py-8 max-h-[calc(100vh-5rem)] overflow-y-auto overflow-x-hidden",
          collapsed ? "sidebar-scroll-hidden" : "sidebar-scroll-dark",
        )}
      >
        <ul className="space-y-2 px-3">
          {menuItems.map((item) => {
            if (!item.access) return null

            const isActive = isPathActive(item.path)
            const isExpanded = !!expandedItems[item.path]
            const hasSubItems = item.subItems && item.subItems.length > 0

            const baseItemCls = cx(
              "w-full flex items-center px-4 py-3 text-sm rounded-2xl transition-all duration-200 select-none",
              isActive
                ? "bg-yellow-900/20 backdrop-blur-sm text-yellow-400"
                : "text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-100",
              collapsed ? "justify-center" : "justify-start",
            )

            if (hasSubItems) {
              return (
                <li key={item.path} className="relative">
                  {/* Parent */}
                  <div
                    onClick={(e) => toggleExpand(item.path, e)}
                    onMouseEnter={(e) => {
                      openFlyout(item, e.currentTarget)
                      openTip(item.name, e.currentTarget)
                    }}
                    onMouseLeave={() => {
                      scheduleCloseFlyout()
                      closeTip()
                    }}
                    className={cx(baseItemCls, "cursor-pointer")}
                  >
                    <div className={cx("flex items-center w-full", collapsed ? "justify-center" : "")}>
                      <span className={cx("inline-flex", isActive ? "text-yellow-400" : "group-hover:text-neutral-100")}>
                        {item.icon}
                      </span>
                      {!collapsed && <span className="ml-3 font-[300]">{item.name}</span>}
                    </div>

                    {!collapsed && (
                      <CaretDown size={16} className={cx("transition-transform duration-300", isExpanded ? "rotate-180" : "")} />
                    )}
                  </div>

                  {/* Sub-items (full mode) */}
                  {!collapsed && (
                    <div
                      className={cx(
                        "overflow-hidden transition-all duration-300 ease-in-out",
                        isExpanded ? "max-h-64 opacity-100 pointer-events-auto" : "max-h-0 opacity-0 pointer-events-none",
                      )}
                    >
                      <div className="mt-2 ml-6 pl-3 border-l border-neutral-800/60 space-y-1">
                        <ul className="space-y-1">
                          {item.subItems.map((sub) => {
                            const isSubActive = location.pathname === sub.path
                            return (
                              <li key={sub.path}>
                                <Link
                                  to={sub.path}
                                  className={cx(
                                    "flex items-center gap-2 px-3 py-2 text-[13px] rounded-2xl transition-all duration-200 hover:translate-x-1",
                                    isSubActive
                                      ? "bg-yellow-900/20 text-yellow-400"
                                      : "text-neutral-400 hover:bg-neutral-800/50 hover:text-neutral-100",
                                  )}
                                >
                                  <span className="opacity-80">{sub.icon}</span>
                                  <span className="font-[300]">{sub.name}</span>
                                </Link>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    </div>
                  )}
                </li>
              )
            }

            return (
              <li key={item.path} className="relative text-start">
                <Link
                  to={item.path}
                  onMouseEnter={(e) => openTip(item.name, e.currentTarget)}
                  onMouseLeave={closeTip}
                  className={cx(baseItemCls, collapsed ? "justify-center" : "", "cursor-pointer")}
                >
                  <span className={cx("inline-flex", isActive ? "text-yellow-400" : "group-hover:text-neutral-100")}>
                    {item.icon}
                  </span>

                  {!collapsed && <span className="ml-3 text-start font-[300]">{item.name}</span>}

                  {!collapsed && item.path === "/leads" && <LeadsBadge count={leadsUnreadCount} />}

                  {/* Collapsed dot for leads */}
                  {collapsed && item.path === "/leads" && (
                    <>
                      {!leadsReadyRef.current && leadsUnreadLoading ? (
                        <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-yellow-500/60 animate-pulse" />
                      ) : leadsUnreadCount > 0 ? (
                        <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-yellow-500" />
                      ) : null}
                    </>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      </div>

      {/* ✅ Collapsed flyout (portal) */}
      {collapsed && flyout?.items?.length && typeof document !== "undefined"
        ? createPortal(
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 4 }}
            transition={{ duration: 0.12 }}
            onMouseEnter={() => flyoutCloseT.current && clearTimeout(flyoutCloseT.current)}
            onMouseLeave={scheduleCloseFlyout}
            className="fixed z-[99999]"
            style={{
              left: SIDEBAR_COLLAPSED_PX + 12,
              top: Math.max(12, Math.min(flyout.rect.top, window.innerHeight - 320)),
              width: 260,
            }}
          >
            <div className="bg-white transition-all duration-300 border-neutral-100 dark:bg-black/30 backdrop-blur-md rounded-2xl border dark:border-neutral-900 shadow-2xl overflow-hidden">
              <div className="px-5 py-3 text-base font-[300] text-neutral-800 dark:text-neutral-100 border-b dark:border-neutral-900 border-neutral-100">
                {flyout.title}
              </div>

              <div className="max-h-[55vh] overflow-y-auto overflow-x-hidden sidebar-scroll-dark">
                {flyout.items.map((sub) => {
                  const isSubActive = location.pathname === sub.path
                  return (
                    <Link
                      key={sub.path}
                      to={sub.path}
                      className={cx(
                        "flex items-center gap-3 px-5 py-3 text-[13px] font-[300] transition-colors",
                        isSubActive
                          ? "dark:bg-yellow-900/20 bg-yellow-100/60 text-yellow-600 dark:text-yellow-400"
                          : "text-neutral-800 dark:text-neutral-100 hover:bg-neutral-100/60 dark:hover:bg-neutral-800/60 hover:text-neutral-700",
                      )}
                    >
                      <span className="opacity-80">{sub.icon}</span>
                      <span>{sub.name}</span>
                    </Link>
                  )
                })}
              </div>
            </div>
          </motion.div>,
          document.body,
        )
        : null}

      {/* ✅ Collapsed tooltip (portal + AnimatePresence INSIDE portal = reliable) */}
      {typeof document !== "undefined"
        ? createPortal(
          <AnimatePresence>
            {collapsed && tip?.text ? (
              <motion.div
                key={tip.text}
                initial={{ opacity: 0, x: -10, scale: 0.98 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -10, scale: 0.98 }}
                transition={{ duration: 0.14 }}
                className="fixed z-[100000] pointer-events-none"
                style={{
                  top: Math.max(12, Math.min(tip.top, window.innerHeight - 12)),
                  left: tip.left,
                  transform: "translateY(-50%)",
                  width: 230,
                }}
              >
                <div className="relative">
                  {/* arrow */}
                  <span className="absolute -left-1 top-4 -translate-y-1/2 w-2 h-2  rotate-45 bg-white/20 backdrop-blur-md dark:bg-neutral-950/90 ring-1 ring-white/5" />

                  {/* bubble */}
                  <div className="rounded-2xl px-4 py-2.5 w-fit  bg-white/10  dark:bg-black/10 backdrop-blur-sm ring-1 ring-white/5 shadow-[0_18px_60px_-20px_rgba(0,0,0,0.9)]">
                    <div className="text-[12.5px] font-[350] tracking-wide text-neutral-800 dark:text-neutral-200">{tip.text}</div>
                    <div className="mt-0.5 text-[11px] text-neutral-600 dark:text-neutral-500">Click to open</div>
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.body,
        )
        : null}
    </div>
  )
}

export default Sidebar
